'use client';

import React from 'react';
import PropertyInfo from './PropertyInfo';
import { CheckCircle, AlertTriangle, Info } from 'lucide-react';

const FEATURES = [
  { id: 'feat-otopark', label: 'Kapalı Otopark', available: true },
  { id: 'feat-asansor', label: 'Asansör', available: true },
  { id: 'feat-balkon', label: 'Balkon', available: true },
  { id: 'feat-site', label: 'Site İçerisinde', available: true },
  { id: 'feat-guvenlik', label: '7/24 Güvenlik', available: true },
  { id: 'feat-havuz', label: 'Yüzme Havuzu', available: true },
  { id: 'feat-spor', label: 'Spor Salonu', available: true },
  { id: 'feat-konserj', label: 'Konsiyerj', available: true },
  { id: 'feat-depo', label: 'Depo', available: true },
  { id: 'feat-bahce', label: 'Ortak Bahçe', available: true },
  { id: 'feat-jenerator', label: 'Jeneratör', available: false },
  { id: 'feat-sauna', label: 'Sauna', available: false },
];

interface PropertyTabsProps {
  property: {
    id: string;
    investmentScore: number;
    rentalYield: string;
  };
}

export default function PropertyTabs({ property }: PropertyTabsProps) {
  return (
    <div className="space-y-8">
      {/* Property Specs */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4">İlan Detayları</h3>
        <PropertyInfo />
      </div>

      {/* Description */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3">İlan Açıklaması</h3>
        <div className="card-base p-5">
          <p className="text-sm text-foreground leading-relaxed mb-3">
            Kadıköy Moda&apos;nın kalbinde, deniz manzaralı bu lüks 3+1 daire; 2022 yapımı, 12 katlı prestijli bir sitede 8. katta yer almaktadır. 150 m² net kullanım alanına sahip daire, açık plan mutfak-salon konsepti, 2 tam banyo ve geniş balkonuyla modern yaşam standartlarını yansıtmaktadır.
          </p>
          <p className="text-sm text-foreground leading-relaxed mb-3">
            Güney-doğu cepheli daire; sabah güneşinden ve Marmara Denizi manzarasından tam olarak yararlanmaktadır. Ebeveyn banyosu jakuzili olup, mutfak yerli ve yabancı markalı ankastre beyaz eşyalarla donatılmıştır.
          </p>
          <p className="text-sm text-foreground leading-relaxed">
            Site içinde 7/24 güvenlik, kapalı otopark (2 araçlık), yüzme havuzu, spor salonu ve konsiyerj hizmetleri mevcuttur. Kadıköy sahiline 5 dakika yürüme mesafesi, metroya 8 dakika yürüme mesafesindedir.
          </p>
          <div className="mt-3 p-3 rounded-xl bg-primary-light border border-primary/20 flex items-start gap-2">
            <Info size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-primary font-medium">Bu açıklama Evora AI tarafından ilan sahibinin bilgileri doğrultusunda oluşturulmuştur. İlan sahibi tarafından onaylanmıştır.</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4">Özellikler ve İmkanlar</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {FEATURES.map((feat) => (
            <div
              key={feat.id}
              className={`flex items-center gap-2.5 p-3 rounded-xl border ${feat.available ? 'border-border bg-white' : 'border-border bg-muted opacity-50'}`}
            >
              {feat.available
                ? <CheckCircle size={15} className="text-success flex-shrink-0" />
                : <div className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
              }
              <span className={`text-sm font-medium ${feat.available ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                {feat.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Earthquake Info */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3">Bina Bilgisi</h3>
        <div className="card-base p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {[
              { label: 'Bina Yaşı', value: '4 Yıl (2022)' },
              { label: 'Yapı Tipi', value: 'Betonarme' },
              { label: 'Deprem Yönetmeliği', value: '2018 Yönetmeliği' },
              { label: 'Zemin Etüdü', value: 'Yapıldı' },
              { label: 'İskan Belgesi', value: 'Mevcut' },
              { label: 'Yapı Denetim', value: 'Onaylı' },
            ].map((item, i) => (
              <div key={`bina-${i}`}>
                <p className="text-xs text-muted-foreground font-medium">{item.label}</p>
                <p className="text-sm font-bold text-foreground mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="p-3 rounded-xl bg-warning-light border border-warning/20 flex items-start gap-2">
            <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning font-medium">
              Bina bilgileri ilan sahibi tarafından sağlanmıştır. Resmi doğrulama için ilgili belediye ve tapu müdürlüğüne başvurmanızı öneririz.
            </p>
          </div>
        </div>
      </div>

      {/* Price History */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3">Fiyat Geçmişi</h3>
        <div className="card-base overflow-hidden">
          <div className="grid grid-cols-3 px-4 py-2.5 bg-muted border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <span>Tarih</span>
            <span className="text-center">Fiyat</span>
            <span className="text-right">Değişim</span>
          </div>
          {[
            { id: 'ph-001', date: '25 Ağu 2026', price: '₺12.750.000', change: 'Mevcut', changeColor: 'text-foreground' },
            { id: 'ph-002', date: '10 Tem 2026', price: '₺13.200.000', change: '-₺450.000', changeColor: 'text-danger' },
            { id: 'ph-003', date: '15 May 2026', price: '₺13.200.000', change: 'İlan açıldı', changeColor: 'text-muted-foreground' },
          ].map((ph) => (
            <div key={ph.id} className="grid grid-cols-3 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
              <span className="text-sm text-muted-foreground">{ph.date}</span>
              <span className="text-sm font-bold text-foreground text-center tabular-nums">{ph.price}</span>
              <span className={`text-sm font-semibold text-right tabular-nums ${ph.changeColor}`}>{ph.change}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Yalnızca Evora platformundaki fiyat değişimleri gösterilmektedir.</p>
      </div>
    </div>
  );
}